export type OsanoRawConsent = Record<string, string>;

export type OsanoConsentWindow = Window & {
  Osano?: {
    cm?: {
      getConsent: () => OsanoRawConsent;
    };
  };
  OnetrustActiveGroups?: string;
};

export type NormalizedConsent = {
  cmp: 'osano' | null;
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
  saleOptOut: boolean;
};

const DENIED: Omit<NormalizedConsent, 'cmp' | 'essential'> = {
  analytics: false,
  marketing: false,
  personalization: false,
  saleOptOut: true,
};

// Bridge from Osano's consent state to the legacy OneTrust group-code array
// that existing consumers still read via the `OneTrust` key (clickout
// payloads, CSI, ConsentMode). Remove once those consumers read `Consent`
// directly. Mirrors the mapping used in bx-international-sylar PR #2028.
const OT_BRIDGE: Array<[string, (c: OsanoRawConsent) => boolean]> = [
  ['C0001', () => true], // Strictly Necessary -> Essential
  ['C0002', (c) => c.ANALYTICS === 'ACCEPT'], // Performance -> Analytics
  ['C0003', (c) => c.PERSONALIZATION === 'ACCEPT'], // Functional -> Personalization
  ['C0004', (c) => c.MARKETING === 'ACCEPT'], // Targeting -> Marketing
  ['C0005', (c) => c.MARKETING === 'ACCEPT'], // Social Media -> Marketing
  ['C0007', (c) => c.MARKETING === 'ACCEPT'], // Behavioral Advertising -> Marketing
];

export const getOsanoConsent = (
  windowObj: OsanoConsentWindow
): NormalizedConsent => {
  const raw = windowObj?.Osano?.cm?.getConsent?.();
  if (!raw) {
    return { cmp: null, essential: true, ...DENIED };
  }

  return {
    cmp: 'osano',
    essential: true,
    analytics: raw.ANALYTICS === 'ACCEPT',
    marketing: raw.MARKETING === 'ACCEPT',
    personalization: raw.PERSONALIZATION === 'ACCEPT',
    saleOptOut: raw['OPT-OUT'] === 'ACCEPT',
  };
};

// Returns the legacy OneTrust active-group codes, bridged from Osano's live
// consent state when Osano is loaded, or read directly from the OneTrust
// global otherwise (flag off, or Osano script not yet loaded).
export const getLegacyOneTrustGroups = (
  windowObj: OsanoConsentWindow
): string[] | null => {
  const raw = windowObj?.Osano?.cm?.getConsent?.();
  if (raw) {
    return OT_BRIDGE.filter(([, test]) => test(raw)).map(([code]) => code);
  }

  return (
    windowObj?.OnetrustActiveGroups?.split?.(',')?.filter?.((x: string) => x) ||
    null
  );
};
